import Axios, { AxiosInstance } from 'axios';
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import * as Sentry from '@sentry/react';

const ApiContext = createContext<AxiosInstance>({} as AxiosInstance);
export const ApiProvider: React.FC<ReactNode> = ({ children }) => {
  const { accessToken } = useOidcAccessToken();
  const api = useMemo(() => {
    const axios = Axios.create({
      baseURL: import.meta.env.VITE_REPORTING_API,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    axios.interceptors.request.use((config) => {
      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    });

    axios.interceptors.response.use(
      (response) => {
        const data = response.data;
        if (data && typeof data === 'object' && data.isFailed === true) {
          const rawUrl = response.config?.url;
          const url = typeof rawUrl === 'string' ? rawUrl.split('?')[0] : 'unknown';
          const messages = Array.isArray(data.reasons)
            ? data.reasons.map((r: { message?: string }) => r?.message).filter(Boolean).join('; ')
            : 'unknown FluentResults failure';
          Sentry.captureMessage(`FluentResults failure: ${messages || 'no message'}`, {
            level: 'warning',
            tags: {
              url,
              method: response.config?.method ?? 'unknown',
              status: String(response.status),
              kind: 'fluentresults',
            },
          });
        }
        return response;
      },
      (error) => {
        if (Axios.isCancel(error)) {
          return Promise.reject(error);
        }
        const rawUrl = error.config?.url;
        const url = typeof rawUrl === 'string' ? rawUrl.split('?')[0] : 'unknown';
        Sentry.captureException(error, {
          tags: {
            url,
            method: error.config?.method ?? 'unknown',
            status: String(error.response?.status ?? 'network'),
          },
        });
        return Promise.reject(error);
      },
    );

    return axios;
  }, []);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
};

export function useApi(): AxiosInstance {
  return useContext(ApiContext);
}
